import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { Pagination, usePaginationState } from '../components/common/Pagination';
import {
  RefreshCw, Activity, Shield, Clock, CheckCircle, AlertTriangle,
  Zap, BarChart3, Key, Server, TrendingUp, Globe,
} from 'lucide-react';
import * as api from '../services/api';
import PageHeader from '../components/common/PageHeader';
/* G102 — full v2 redesign of GatewayDashboardPage. Replaces
 * GovPageLayout + custom TabBar with V2PageShell + V2TabNav. */
import {
  V2PageShell,
  TabNav as V2TabNav,
  Button as V2Button,
  Pill as V2Pill,
} from '../components/v2';
import {
  BRAND, S, ACCENT,
  GovStyles, GovPageLayout, TabBar, StatCard,
  Card, RefreshButton, GhostButton, Badge,
} from '../components/common/GovernanceDesignSystem';

// ---------------------------------------------------------------------------
// Helper
// ---------------------------------------------------------------------------
const unwrap = (res) => {
  if (res && typeof res === 'object' && 'data' in res) return res.data;
  return res;
};

const EVENT_TYPE_COLORS = {
  CREATED: { bg: '#dcfce7', text: '#166534' },
  ROTATED: { bg: '#fef9c3', text: '#854d0e' },
  REVOKED: { bg: '#f5f5f4', text: '#78716c' },
  EXPIRED: { bg: '#f5f5f4', text: '#78716c' },
  SUSPENDED: { bg: '#fee2e2', text: '#991b1b' },
  REACTIVATED: { bg: '#dcfce7', text: '#166534' },
  SCOPE_CHANGED: { bg: '#e0e7ff', text: '#3730a3' },
  RATE_LIMIT_HIT: { bg: '#fef9c3', text: '#854d0e' },
  AUTH_FAILURE: { bg: '#fee2e2', text: '#991b1b' },
  IP_BLOCKED: { bg: '#fee2e2', text: '#991b1b' },
  QUOTA_EXCEEDED: { bg: '#fef9c3', text: '#854d0e' },
};

// ---------------------------------------------------------------------------
// Inline Event Badge
// ---------------------------------------------------------------------------
function EventBadge({ eventType }) {
  const colors = EVENT_TYPE_COLORS[eventType] || { bg: S[100], text: S[600] };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', padding: '2px 10px', borderRadius: 100,
      fontSize: 11, fontWeight: 600, background: colors.bg, color: colors.text,
    }}>
      {eventType}
    </span>
  );
}

// ---------------------------------------------------------------------------
// MAIN COMPONENT
// ---------------------------------------------------------------------------
export default function GatewayDashboardPage() {
  const { user } = useAuth();
  const { addToast } = useToast();

  const [tab, setTab] = useState('overview');

  // Data
  const [keys, setKeys] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [auditLoading, setAuditLoading] = useState(false);

  const { page: auditPage, pageSize: auditPageSize, setPage: setAuditPage } = usePaginationState(25);

  // ── Data Loading ─────────────────────────────────────────────────────────

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      const [keysRes, healthRes] = await Promise.allSettled([
        api.gatewayListKeys(),
        api.gatewayHealth(),
      ]);

      if (keysRes.status === 'fulfilled') {
        const data = unwrap(keysRes.value);
        setKeys(Array.isArray(data) ? data : []);
      }

      if (healthRes.status === 'fulfilled') {
        setHealthData(unwrap(healthRes.value));
      }
    } catch (err) {
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAudit = useCallback(async () => {
    try {
      setAuditLoading(true);
      const data = unwrap(await api.gatewayGetTenantAudit());
      setAuditLogs(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Audit load error:', err);
      addToast('Failed to load audit trail', 'error');
    } finally {
      setAuditLoading(false);
    }
  }, [addToast]);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    if (tab === 'audit' && auditLogs.length === 0) {
      loadAudit();
    }
  }, [tab, auditLogs.length, loadAudit]);

  // ── Computed Stats ────────────────────────────────────────────────────────

  const keyStats = useMemo(() => {
    const active = keys.filter((k) => k.status === 'ACTIVE');
    const totalRequests = keys.reduce((sum, k) => sum + (k.totalRequests || 0), 0);
    const totalBlocked = keys.reduce((sum, k) => sum + (k.blockedRequests || 0), 0);
    const totalErrors = keys.reduce((sum, k) => sum + (k.errorCount || 0), 0);
    const gatewayKeys = keys.filter((k) => k.keyType === 'GATEWAY').length;
    const sdkKeys = keys.filter((k) => k.keyType === 'SDK').length;

    return {
      total: keys.length,
      active: active.length,
      suspended: keys.filter((k) => k.status === 'SUSPENDED').length,
      rotating: keys.filter((k) => k.status === 'ROTATING').length,
      revoked: keys.filter((k) => k.status === 'REVOKED' || k.status === 'EXPIRED').length,
      totalRequests,
      totalBlocked,
      totalErrors,
      gatewayKeys,
      sdkKeys,
      successRate: totalRequests > 0
        ? (((totalRequests - totalErrors) / totalRequests) * 100).toFixed(1)
        : '100.0',
    };
  }, [keys]);

  const topKeys = useMemo(() => {
    return [...keys]
      .filter((k) => k.status === 'ACTIVE')
      .sort((a, b) => (b.totalRequests || 0) - (a.totalRequests || 0))
      .slice(0, 10);
  }, [keys]);

  const auditStats = useMemo(() => {
    const counts = {};
    auditLogs.forEach((log) => {
      counts[log.eventType] = (counts[log.eventType] || 0) + 1;
    });
    return counts;
  }, [auditLogs]);

  const paginatedAudit = useMemo(() => {
    const start = (auditPage - 1) * auditPageSize;
    return auditLogs.slice(start, start + auditPageSize);
  }, [auditLogs, auditPage, auditPageSize]);

  // ── Formatting ────────────────────────────────────────────────────────────

  const fmtDate = (d) => {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    } catch { return String(d); }
  };

  const fmtNumber = (n) => (n == null ? '0' : Number(n).toLocaleString());

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════

  if (loading) {
    return (
      <V2PageShell
        title="Gateway · Dashboard"
        subtitle="Loading…"
        tags={<V2Pill tone="cyan" size="sm">Gateway</V2Pill>}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: S[500] }}>
          Loading Gateway Dashboard…
        </div>
      </V2PageShell>
    );
  }

  const tabDefinitions = [
    { key: 'overview', label: 'Overview', icon: BarChart3 },
    { key: 'keys',     label: 'Active Keys', icon: Key },
    { key: 'security', label: 'Security Events', icon: Shield },
    { key: 'audit',    label: 'Audit Trail', icon: Clock },
  ];
  /* G102 — v2 TabNav config (drop the icons since v2 TabNav owns visuals) */
  const v2Tabs = tabDefinitions.map(t => ({ id: t.key, label: t.label }));

  return (
    <V2PageShell
      title="Gateway · Dashboard"
      subtitle="Live traffic and policy enforcement at the protected gateway."
      tags={<V2Pill tone="cyan" size="sm">Gateway</V2Pill>}
      actions={
        <V2Button
          variant="ghost"
          size="sm"
          iconLeft={<RefreshCw size={13} />}
          onClick={() => { loadAll(); if (tab === 'audit') loadAudit(); }}
        >
          Refresh
        </V2Button>
      }
    >
      {/* Health Status Banner */}
      {healthData && (
        <Card style={{ marginBottom: 24, borderLeft: `4px solid ${healthData.status === 'UP' ? ACCENT.emerald.icon : ACCENT.rose.icon}`, display: 'flex', alignItems: 'center', gap: 16, padding: 16 }}>
          <div style={{
            width: 40, height: 40, borderRadius: '50%',
            background: healthData.status === 'UP' ? ACCENT.emerald.bg : ACCENT.rose.bg,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {healthData.status === 'UP'
              ? <CheckCircle size={20} color={ACCENT.emerald.icon} />
              : <AlertTriangle size={20} color={ACCENT.rose.icon} />}
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: S[900] }}>
              Gateway Status: {healthData.status === 'UP' ? 'Operational' : 'Degraded'}
            </div>
            <div style={{ fontSize: 12, color: S[500] }}>
              {healthData.message || 'All systems nominal'}
            </div>
          </div>
        </Card>
      )}

      {/* Tabs — v2 TabNav (G102) */}
      <V2TabNav
        tabs={v2Tabs}
        activeId={tab}
        onChange={setTab}
        ariaLabel="Gateway dashboard sections"
      />
      <div className="my-4" />

      {/* ── Tab: Overview ──────────────────────────────────────────────────── */}
      {tab === 'overview' && (
        <>
          {/* Primary Stats Row */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
            <StatCard
              label="Total Requests"
              value={fmtNumber(keyStats.totalRequests)}
              subtitle="Across all keys"
              accent={ACCENT.slate}
            />
            <StatCard
              label="Success Rate"
              value={`${keyStats.successRate}%`}
              subtitle={`${fmtNumber(keyStats.totalErrors)} errors`}
              accent={ACCENT.emerald}
            />
            <StatCard
              label="Blocked"
              value={fmtNumber(keyStats.totalBlocked)}
              subtitle="Rate limit + IP blocks"
              accent={ACCENT.rose}
            />
            <StatCard
              label="Active Keys"
              value={keyStats.active}
              subtitle={`${keyStats.total} total`}
              accent={ACCENT.indigo}
            />
          </div>

          {/* Key Distribution */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
            <Card style={{ padding: 20 }}>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: S[900], margin: '0 0 16px 0' }}>Key Distribution</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {[
                  { label: 'Active', value: keyStats.active, color: ACCENT.emerald.icon },
                  { label: 'Rotating', value: keyStats.rotating, color: ACCENT.amber.icon },
                  { label: 'Suspended', value: keyStats.suspended, color: ACCENT.rose.icon },
                  { label: 'Revoked / Expired', value: keyStats.revoked, color: S[400] },
                ].map(({ label, value, color }) => (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 12, height: 12, borderRadius: 3, background: color, flexShrink: 0 }} />
                    <div style={{ flex: 1, fontSize: 13, color: S[600] }}>{label}</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: S[900] }}>{value}</div>
                    {keyStats.total > 0 && (
                      <div style={{
                        width: 80, height: 6, background: S[100], borderRadius: 3, overflow: 'hidden',
                      }}>
                        <div style={{
                          width: `${(value / keyStats.total) * 100}%`,
                          height: '100%', background: color, borderRadius: 3,
                        }} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Card>

            <Card style={{ padding: 20 }}>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: S[900], margin: '0 0 16px 0' }}>Key Types</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 16, padding: 16,
                  background: S[50], borderRadius: 8,
                }}>
                  <div style={{
                    width: 48, height: 48, borderRadius: 10, background: BRAND.light,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Server size={22} color={BRAND.primary} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: S[900] }}>Gateway Keys</div>
                    <div style={{ fontSize: 12, color: S[500] }}>Full proxy invocation</div>
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: S[900] }}>{keyStats.gatewayKeys}</div>
                </div>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 16, padding: 16,
                  background: S[50], borderRadius: 8,
                }}>
                  <div style={{
                    width: 48, height: 48, borderRadius: 10, background: ACCENT.violet.bg,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Zap size={22} color={ACCENT.violet.icon} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: S[900] }}>SDK Keys</div>
                    <div style={{ fontSize: 12, color: S[500] }}>Split pre/post protection</div>
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: S[900] }}>{keyStats.sdkKeys}</div>
                </div>
              </div>
            </Card>
          </div>

          {/* Top Active Keys by Usage */}
          <Card style={{ padding: 20 }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: S[900], margin: '0 0 16px 0' }}>Top Active Keys by Usage</h2>
            {topKeys.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 32, color: S[500] }}>
                No active keys with traffic yet.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: S[400], textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `1px solid ${S[200]}`, background: S[50] }}>Name</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: S[400], textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `1px solid ${S[200]}`, background: S[50] }}>Prefix</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: S[400], textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `1px solid ${S[200]}`, background: S[50] }}>Type</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: S[400], textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `1px solid ${S[200]}`, background: S[50] }}>Requests</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: S[400], textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `1px solid ${S[200]}`, background: S[50] }}>Blocked</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: S[400], textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `1px solid ${S[200]}`, background: S[50] }}>Errors</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: S[400], textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `1px solid ${S[200]}`, background: S[50] }}>Rate Limit</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: S[400], textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `1px solid ${S[200]}`, background: S[50] }}>Last Used</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topKeys.map((key) => (
                      <tr key={key.id} className="gov-row">
                        <td style={{ padding: '12px 16px', fontSize: 13, borderBottom: `1px solid ${S[100]}` }}>
                          <span style={{ fontWeight: 600, color: S[900] }}>{key.name}</span>
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: 13, borderBottom: `1px solid ${S[100]}` }}>
                          <span style={{
                            fontFamily: "'IBM Plex Mono', monospace", fontSize: 12,
                            background: S[100], padding: '2px 6px', borderRadius: 4,
                          }}>
                            {key.keyPrefix || '—'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: 13, borderBottom: `1px solid ${S[100]}` }}>
                          <span style={{ fontSize: 12, color: S[600] }}>
                            {key.keyType === 'SDK' ? 'SDK' : 'Gateway'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: 13, borderBottom: `1px solid ${S[100]}` }}>
                          <span style={{ fontWeight: 600, color: S[900] }}>{fmtNumber(key.totalRequests)}</span>
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: 13, borderBottom: `1px solid ${S[100]}` }}>
                          <span style={{ color: (key.blockedRequests || 0) > 0 ? ACCENT.rose.icon : S[400] }}>
                            {fmtNumber(key.blockedRequests)}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: 13, borderBottom: `1px solid ${S[100]}` }}>
                          <span style={{ color: (key.errorCount || 0) > 0 ? ACCENT.amber.icon : S[400] }}>
                            {fmtNumber(key.errorCount)}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: 13, borderBottom: `1px solid ${S[100]}` }}>
                          <span style={{ fontSize: 12, color: S[600] }}>{key.rateLimit || 60} RPM</span>
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: 13, borderBottom: `1px solid ${S[100]}`, color: S[400] }}>
                          {fmtDate(key.lastUsedAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}

      {/* ── Tab: Active Keys ───────────────────────────────────────────────── */}
      {tab === 'keys' && (
        <Card style={{ padding: 20 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: S[900], margin: '0 0 16px 0' }}>All Active Keys</h2>
          {keys.filter((k) => k.status === 'ACTIVE').length === 0 ? (
            <div style={{ textAlign: 'center', padding: 32, color: S[500] }}>No active keys.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: S[400], textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `1px solid ${S[200]}`, background: S[50] }}>Name</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: S[400], textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `1px solid ${S[200]}`, background: S[50] }}>Prefix</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: S[400], textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `1px solid ${S[200]}`, background: S[50] }}>Type</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: S[400], textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `1px solid ${S[200]}`, background: S[50] }}>Rate Limit</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: S[400], textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `1px solid ${S[200]}`, background: S[50] }}>Burst</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: S[400], textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `1px solid ${S[200]}`, background: S[50] }}>Scoped Agents</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: S[400], textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `1px solid ${S[200]}`, background: S[50] }}>IP Allowlist</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: S[400], textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `1px solid ${S[200]}`, background: S[50] }}>Requests</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: S[400], textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `1px solid ${S[200]}`, background: S[50] }}>Expires</th>
                  </tr>
                </thead>
                <tbody>
                  {keys.filter((k) => k.status === 'ACTIVE').map((key) => (
                    <tr key={key.id} className="gov-row">
                      <td style={{ padding: '12px 16px', fontSize: 13, borderBottom: `1px solid ${S[100]}` }}>
                        <div style={{ fontWeight: 600, color: S[900] }}>{key.name}</div>
                        {key.description && (
                          <div style={{ fontSize: 11, color: S[400] }}>{key.description}</div>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 13, borderBottom: `1px solid ${S[100]}` }}>
                        <span style={{
                          fontFamily: "'IBM Plex Mono', monospace", fontSize: 12,
                          background: S[100], padding: '2px 6px', borderRadius: 4,
                        }}>
                          {key.keyPrefix || '—'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 13, borderBottom: `1px solid ${S[100]}` }}>{key.keyType === 'SDK' ? 'SDK' : 'Gateway'}</td>
                      <td style={{ padding: '12px 16px', fontSize: 13, borderBottom: `1px solid ${S[100]}` }}>{key.rateLimit || 60} RPM</td>
                      <td style={{ padding: '12px 16px', fontSize: 13, borderBottom: `1px solid ${S[100]}` }}>{key.burstLimit || 10} RPS</td>
                      <td style={{ padding: '12px 16px', fontSize: 13, borderBottom: `1px solid ${S[100]}` }}>
                        <span style={{ fontSize: 12, color: S[600] }}>
                          {Array.isArray(key.scopedAgentIds) && key.scopedAgentIds.length > 0
                            ? `${key.scopedAgentIds.length} agent(s)`
                            : 'All agents'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 13, borderBottom: `1px solid ${S[100]}` }}>
                        <span style={{ fontSize: 12, color: S[600] }}>
                          {Array.isArray(key.ipAllowlist) && key.ipAllowlist.length > 0
                            ? `${key.ipAllowlist.length} rule(s)`
                            : 'Any IP'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 13, borderBottom: `1px solid ${S[100]}`, fontWeight: 600 }}>{fmtNumber(key.totalRequests)}</td>
                      <td style={{ padding: '12px 16px', fontSize: 13, borderBottom: `1px solid ${S[100]}`, color: S[400] }}>{fmtDate(key.expiresAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* ── Tab: Security Events ──────────────────────────────────────────── */}
      {tab === 'security' && (
        <>
          {/* Security stats */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
            <StatCard
              label="Auth Failures"
              value={fmtNumber(auditStats['AUTH_FAILURE'] || 0)}
              accent={ACCENT.rose}
            />
            <StatCard
              label="IP Blocked"
              value={fmtNumber(auditStats['IP_BLOCKED'] || 0)}
              accent={ACCENT.rose}
            />
            <StatCard
              label="Rate Limits Hit"
              value={fmtNumber(auditStats['RATE_LIMIT_HIT'] || 0)}
              accent={ACCENT.amber}
            />
            <StatCard
              label="Keys Revoked"
              value={fmtNumber(auditStats['REVOKED'] || 0)}
              accent={ACCENT.slate}
            />
          </div>

          {/* Security-relevant audit log */}
          <Card style={{ padding: 20 }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: S[900], margin: '0 0 16px 0' }}>Security Event Log</h2>
            {auditLoading ? (
              <div style={{ textAlign: 'center', padding: 32, color: S[500] }}>Loading...</div>
            ) : (
              (() => {
                const securityEvents = auditLogs.filter((l) =>
                  ['AUTH_FAILURE', 'IP_BLOCKED', 'RATE_LIMIT_HIT', 'REVOKED', 'SUSPENDED', 'QUOTA_EXCEEDED'].includes(l.eventType)
                );
                return securityEvents.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 32, color: S[500] }}>
                    No security events recorded. This is good!
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: S[400], textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `1px solid ${S[200]}`, background: S[50] }}>Event</th>
                          <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: S[400], textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `1px solid ${S[200]}`, background: S[50] }}>Key</th>
                          <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: S[400], textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `1px solid ${S[200]}`, background: S[50] }}>Source IP</th>
                          <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: S[400], textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `1px solid ${S[200]}`, background: S[50] }}>Details</th>
                          <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: S[400], textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `1px solid ${S[200]}`, background: S[50] }}>Timestamp</th>
                        </tr>
                      </thead>
                      <tbody>
                        {securityEvents.slice(0, 50).map((log, i) => (
                          <tr key={log.id || i} className="gov-row">
                            <td style={{ padding: '12px 16px', fontSize: 13, borderBottom: `1px solid ${S[100]}` }}><EventBadge eventType={log.eventType} /></td>
                            <td style={{ padding: '12px 16px', fontSize: 13, borderBottom: `1px solid ${S[100]}` }}>
                              <span style={{
                                fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
                                background: S[100], padding: '2px 6px', borderRadius: 4,
                              }}>
                                {log.apiKeyId ? log.apiKeyId.substring(0, 8) + '...' : '—'}
                              </span>
                            </td>
                            <td style={{ padding: '12px 16px', fontSize: 13, borderBottom: `1px solid ${S[100]}`, color: S[600] }}>
                              {log.sourceIp || '—'}
                            </td>
                            <td style={{ padding: '12px 16px', fontSize: 13, borderBottom: `1px solid ${S[100]}`, color: S[500], maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {log.reason || log.details || '—'}
                            </td>
                            <td style={{ padding: '12px 16px', fontSize: 13, borderBottom: `1px solid ${S[100]}`, color: S[400] }}>
                              {fmtDate(log.createdAt)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()
            )}
          </Card>
        </>
      )}

      {/* ── Tab: Full Audit Trail ─────────────────────────────────────────── */}
      {tab === 'audit' && (
        <Card style={{ padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: S[900], margin: 0 }}>Full Audit Trail</h2>
            <RefreshButton onClick={loadAudit} />
          </div>

          {auditLoading ? (
            <div style={{ textAlign: 'center', padding: 32, color: S[500] }}>Loading audit events...</div>
          ) : auditLogs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 32, color: S[500] }}>No audit events yet.</div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: S[400], textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `1px solid ${S[200]}`, background: S[50] }}>Event</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: S[400], textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `1px solid ${S[200]}`, background: S[50] }}>Key ID</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: S[400], textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `1px solid ${S[200]}`, background: S[50] }}>Actor</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: S[400], textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `1px solid ${S[200]}`, background: S[50] }}>Source IP</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: S[400], textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `1px solid ${S[200]}`, background: S[50] }}>Reason</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: S[400], textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `1px solid ${S[200]}`, background: S[50] }}>Timestamp</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedAudit.map((log, i) => (
                      <tr key={log.id || i} className="gov-row">
                        <td style={{ padding: '12px 16px', fontSize: 13, borderBottom: `1px solid ${S[100]}` }}><EventBadge eventType={log.eventType} /></td>
                        <td style={{ padding: '12px 16px', fontSize: 13, borderBottom: `1px solid ${S[100]}` }}>
                          <span style={{
                            fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
                            background: S[100], padding: '2px 6px', borderRadius: 4,
                          }}>
                            {log.apiKeyId ? log.apiKeyId.substring(0, 8) + '...' : '—'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: 13, borderBottom: `1px solid ${S[100]}`, color: S[600] }}>
                          {log.actorId ? log.actorId.substring(0, 8) + '...' : 'System'}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: 13, borderBottom: `1px solid ${S[100]}`, color: S[600] }}>
                          {log.sourceIp || '—'}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: 13, borderBottom: `1px solid ${S[100]}`, color: S[500], maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {log.reason || log.details || '—'}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: 13, borderBottom: `1px solid ${S[100]}`, color: S[400] }}>
                          {fmtDate(log.createdAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {auditLogs.length > auditPageSize && (
                <div style={{ marginTop: 16 }}>
                  <Pagination
                    totalItems={auditLogs.length}
                    page={auditPage}
                    pageSize={auditPageSize}
                    onPageChange={setAuditPage}
                  />
                </div>
              )}
            </>
          )}
        </Card>
      )}
    </V2PageShell>
  );
}
